import React, { useState } from "react"
import { Button } from "components/button"
import { useMutation, useQueryClient } from "react-query"
import Axios from "axios"
import { useSnackbarContext } from "components/snackbar"
import { MdCloudDownload } from "react-icons/md"
import { useTranslation } from "react-i18next"

type ProductType = "parts" | "services" | "groups"
type StatusType = "active" | "archived" | "both"

const ProductFilesExport = () => {
  const { t } = useTranslation()
  const tBase = "views.admin.export.components.ProductFiles"
  const tr = (key: string) => t(`${tBase}.${key}`)

  const { showSnackbar } = useSnackbarContext()
  const queryClient = useQueryClient()

  const [selectedTypes, setSelectedTypes] = useState<ProductType[]>(["parts", "services", "groups"])
  const [status, setStatus] = useState<StatusType>("active")

  const exportProductFiles = useMutation(
    async () => {
      const response = await Axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/files/export-products`,
        {
          types: selectedTypes,
          status
        }
      )
      return response.data
    },
    {
      onSuccess: () => {
        showSnackbar(
          tr("Export requested! You will receive an email when the export is ready.")
        )
        // Immediately refetch export jobs to show the new export
        queryClient.invalidateQueries("export-jobs")
      },
      onError: (error) => {
        console.error(error)
        showSnackbar(tr("Failed to request export"))
      }
    }
  )

  const handleExport = () => {
    if (selectedTypes.length === 0) {
      showSnackbar(tr("Please select at least one product type"))
      return
    }
    exportProductFiles.mutate()
  }

  const toggleType = (type: ProductType) => {
    setSelectedTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    )
  }

  const toggleAllTypes = () => {
    setSelectedTypes(prev =>
      prev.length === 3 ? [] : ["parts", "services", "groups"]
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold mb-4">{tr("Export Product Files")}</h3>
      <p className="text-gray-600 mb-6">
        {tr("Export all product images (parts, services, groups) from your inventory. Select which product types to include and whether to export active, archived, or all items.")}
      </p>

      <div className="space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Product Types Selection */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {tr("Product Types")}
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={toggleAllTypes}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  selectedTypes.length === 3
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {tr("All")}
              </button>
              <button
                type="button"
                onClick={() => toggleType("parts")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  selectedTypes.includes("parts")
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {tr("Parts")}
              </button>
              <button
                type="button"
                onClick={() => toggleType("services")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  selectedTypes.includes("services")
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {tr("Services")}
              </button>
              <button
                type="button"
                onClick={() => toggleType("groups")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  selectedTypes.includes("groups")
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {tr("Groups")}
              </button>
            </div>
          </div>

          {/* Status Selection */}
          <div className="min-w-[150px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {tr("Status")}
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as StatusType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="active">{tr("Active Only")}</option>
              <option value="archived">{tr("Archived Only")}</option>
              <option value="both">{tr("Both")}</option>
            </select>
          </div>

          {/* Export Button */}
          <div>
            <Button
              primary
              isLoading={exportProductFiles.isLoading}
              onClick={handleExport}
              disabled={selectedTypes.length === 0}
            >
              <MdCloudDownload style={{ marginRight: "8px", fontSize: "20px" }} />
              {tr("Request Export")}
            </Button>
          </div>
        </div>

        <div className="mt-4 p-4 bg-blue-50 rounded-md">
          <h4 className="text-sm font-medium text-blue-800 mb-2">
            {tr("Export Structure")}
          </h4>
          <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
            <li>{tr("Parts: Organized by Category > Sub-Category > Image")}</li>
            <li>{tr("Services: Organized by service name")}</li>
            <li>{tr("Groups: Organized by group name")}</li>
            <li>{tr("Only items with images will be included in the export")}</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default ProductFilesExport
